<script setup lang="ts">
import {onMounted, ref} from 'vue';
import {useRouter} from 'vue-router';
import ZZC from '@/views/header/ZZCLogo.vue';
import {getAppVersion} from '@/base/frontendConfig';
import {clearMe, getMePermission} from '@/base/me.js';
import {login, loginCode, start, wachtwoordVergeten} from '@/base/auth.js';
import {LoginAntwoord, Permission, StartAntwoord} from 'common';

type ReportState = { text: string, error: boolean };

// email: only the address; wachtwoord: the password of a known address; code: the mailed login code;
// mail: a set-password mail went out, nothing to type here
type Stap = 'email' | 'wachtwoord' | 'code' | 'mail';

const router      = useRouter();
const email       = ref('');
const password    = ref('');
const stap        = ref<Stap>('email');
const code        = ref('');
const onthoud     = ref(false);
const reportState = ref<ReportState>({
    text : '',
    error: false,
});

let errorTimerId: number | ReturnType<typeof setTimeout>;

onMounted(async () => {
    await permissionRedirect();
});

async function onStart() {
    if (email.value.trim() === '') {
        setReportState(makeError('vul je e-mail in'));
        return;
    }
    const antwoord: StartAntwoord = await start(email.value);
    if (antwoord.status === 'mail') {
        stap.value = 'mail';
    } else if (antwoord.status === 'geblokkeerd') {
        setReportState(makeError('te veel pogingen, wacht even'));
    } else {
        stap.value = 'wachtwoord';
    }
}

function onEnter() {
    switch (stap.value) {
        case 'email':
            onStart();
            break;
        case 'wachtwoord':
            onLogin();
            break;
        case 'code':
            onCode();
            break;
        case 'mail':
            break;
    }
}

async function onLogin() {
    clearMe();
    const antwoord: LoginAntwoord = await login(email.value, password.value);
    if (antwoord.status === 'ok') {
        await permissionRedirect();
    } else if (antwoord.status === 'code') {
        stap.value = 'code';
        setReportState(makeOk('code verstuurd'));
    } else if (antwoord.status === 'geblokkeerd') {
        setReportState(makeError('te veel pogingen, wacht even'));
    } else {
        setReportState(makeError('e-mail/wachtwoord fout'));
    }
}

async function onCode() {
    clearMe();
    const antwoord: LoginAntwoord = await loginCode(email.value, code.value, onthoud.value);
    if (antwoord.status === 'ok') {
        await permissionRedirect();
    } else if (antwoord.status === 'geblokkeerd') {
        setReportState(makeError('te veel pogingen, wacht even'));
    } else if ((antwoord.pogingenOver ?? 0) > 0) {
        setReportState(makeError(`code fout, nog ${antwoord.pogingenOver} pogingen`));
    } else {
        stap.value = 'wachtwoord';
        setReportState(makeError('code verlopen, log opnieuw in'));
    }
}

async function onPwReset() {
    if (email.value.trim() === '') {
        setReportState(makeError('vul je e-mail in'));
        return;
    }
    await wachtwoordVergeten(email.value);
    setReportState(makeOk('als het adres bekend is, is er een mail gestuurd'));
}

function makeError(text: string): ReportState {
    return {
        text,
        error: true,
    };
}

function makeOk(text?: string): ReportState {
    return {
        text : text ?? '',
        error: false,
    };
}

function setReportState(state: ReportState) {
    clrReportState();
    if (state.text) {
        reportState.value = state;
        if (errorTimerId) {
            clearTimeout(errorTimerId);
        }
        errorTimerId = setTimeout(clrReportState, 5_000);
    }
    return !state.error;
}

function clrReportState() {
    reportState.value = makeOk();
    if (errorTimerId) {
        clearTimeout(errorTimerId);
    }
}

async function permissionRedirect() {
    const permissions = await getMePermission();
    if (!permissions) {
        console.log(`you need to login first`);
    } else {
        if (permissions.includes(Permission.admin)) {
            await router.push('/admin');
        } else if (permissions.includes(Permission.report)) {
            await router.push('/report');
        } else if (permissions.includes(Permission.accept)) {
            await router.push('/accept');
        } else if (permissions.includes(Permission.manage_reporters)) {
            await router.push('/report_manager')
        } else if (permissions.includes(Permission.dashboard)) {
            await router.push('/dashboard');
        } else {
            console.error(`permission config problem: you only have permissions: ${JSON.stringify(permissions)}`);
        }
    }
}

</script>

<template>
    <div class="z-50 absolute top-[12px] left-[12px] px-2 text-xs text-center">
        {{ getAppVersion() }}
    </div>
    <div class="min-h-screen flex items-center justify-center">
        <div
            @keydown.enter="onEnter"
            class="relative flex flex-col w-[440px] mx-auto p-8 bg-white rounded-lg shadow-2xl between space-y-4"
        >
            <ZZC/>
            <div class="text-2xl font-bold text-center">
                Rapportage Login
            </div>
            <div v-if="stap === 'email' || stap === 'wachtwoord'">
                <label
                    for="email"
                    class="block text-sm font-medium text-gray-700 cursor-pointer"
                >
                    e-mail
                </label>
                <input
                    id="email"
                    v-model="email"
                    type="text"
                    :disabled="stap === 'wachtwoord'"
                    data-testid="login-email"
                    class="mt-1 px-3 py-1 block w-full rounded-md shadow-sm border border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 disabled:text-gray-500"
                />
            </div>
            <div v-if="stap === 'mail'" class="text-sm text-gray-700" data-testid="login-mail-gestuurd">
                Er is een mail gestuurd naar <b>{{ email }}</b> met een link om je wachtwoord te zetten.
                Log daarna hier in. Na het inloggen met je wachtwoord ontvang je voortaan een inlogcode per mail.
            </div>
            <div v-if="stap === 'wachtwoord'">
                <label
                    for="password"
                    class="block text-sm font-medium text-gray-700 cursor-pointer"
                >
                    wachtwoord
                </label>
                <input
                    id="password"
                    v-model="password"
                    type="password"
                    data-testid="login-password"
                    class="mt-1 px-3 py-1 block w-full rounded-md shadow-sm border border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                />
            </div>
            <div v-if="stap !== 'mail'" class="flex flex-row justify-between items-center w-full">
                <Transition
                    enter-from-class="opacity-0"
                    enter-active-class="transition-opacity duration-300 ease-out"
                    enter-to-class="opacity-100"
                    leave-from-class="opacity-100"
                    leave-active-class="transition-opacity duration-1000 ease-in"
                    leave-to-class="opacity-0"
                >
                    <div v-if="reportState.text" class="py-1 px-2 text-white"
                         :class="reportState.error?'bg-red-600':'bg-green-600'">
                        {{ reportState.text }}
                    </div>
                </Transition>
                <div v-if="!reportState.text" class="py-1 px-2">
                </div>
                <button
                    v-if="stap === 'wachtwoord'"
                    @click="onPwReset"
                    class="w-min hover:bg-blue-700 hover:text-white py-2 px-4 rounded cursor-pointer"
                >
                    Wachtwoord&nbsp;reset
                </button>
            </div>
            <div v-if="stap === 'email'" class="flex items-center">
                <button
                    @click="onStart"
                    data-testid="login-verder-button"
                    class="w-full bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded cursor-pointer"
                >
                    Verder
                </button>
            </div>
            <template v-else-if="stap === 'wachtwoord'">
                <div class="flex items-center">
                    <button
                        @click="onLogin"
                        data-testid="login-button"
                        class="w-full bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded cursor-pointer"
                    >
                        Inloggen
                    </button>
                </div>
                <div class="text-center">
                    <button
                        @click="stap = 'email'"
                        class="text-xs text-gray-500 hover:text-blue-700 cursor-pointer"
                    >
                        ander e-mailadres
                    </button>
                </div>
            </template>
            <div v-else-if="stap === 'mail'" class="text-center">
                <button
                    @click="stap = 'email'"
                    class="text-xs text-gray-500 hover:text-blue-700 cursor-pointer"
                >
                    terug
                </button>
            </div>
            <template v-else>
                <div class="text-sm text-gray-700">
                    We hebben een code gestuurd naar {{ email }}
                </div>
                <div>
                    <label
                        for="code"
                        class="block text-sm font-medium text-gray-700 cursor-pointer"
                    >
                        code
                    </label>
                    <input
                        id="code"
                        v-model="code"
                        type="text"
                        inputmode="numeric"
                        autocomplete="one-time-code"
                        data-testid="login-code"
                        class="mt-1 px-3 py-1 block w-full rounded-md shadow-sm border border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                    />
                </div>
                <div class="flex items-center gap-2">
                    <input
                        id="onthoud"
                        v-model="onthoud"
                        type="checkbox"
                        data-testid="login-onthoud"
                        class="cursor-pointer"
                    />
                    <label for="onthoud" class="text-sm text-gray-700 cursor-pointer">
                        Deze browser 30 dagen vertrouwen
                    </label>
                </div>
                <div class="flex items-center">
                    <button
                        @click="onCode"
                        data-testid="login-code-button"
                        class="w-full bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded cursor-pointer"
                    >
                        Bevestigen
                    </button>
                </div>
                <div class="text-center">
                    <button
                        @click="stap = 'wachtwoord'"
                        class="text-xs text-gray-500 hover:text-blue-700 cursor-pointer"
                    >
                        terug
                    </button>
                </div>
            </template>
        </div>
    </div>
</template>
